import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';
import StatCard from '../components/StatCard';
import ColisCard from '../components/ColisCard';
import { useApp } from '../data/ColisContext';
import { formatDate } from '../utils/helpers';

export default function MesColisPage() {
  const navigate = useNavigate();
  const { colis, myColisIds, clearMyColis } = useApp();
  const [selectedDate, setSelectedDate] = useState('');

  const dateKey = (isoString) => {
    if (!isoString) return '';
    const s = String(isoString);
    return s.length >= 10 ? s.slice(0, 10) : '';
  };

  const filtered = useMemo(() => {
    if (!myColisIds || myColisIds.length === 0) return [];
    const setIds = new Set(myColisIds);
    return colis.filter(c => setIds.has(c.id));
  }, [colis, myColisIds]);

  const availableDates = useMemo(() => {
    const dates = new Set(
      filtered
        .map(c => dateKey(c.date))
        .filter(Boolean)
    );
    return Array.from(dates).sort((a, b) => (a < b ? 1 : -1));
  }, [filtered]);

  const filteredByDate = useMemo(() => {
    if (!selectedDate) return filtered;
    return filtered.filter(c => dateKey(c.date) === selectedDate);
  }, [filtered, selectedDate]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    for (const c of filteredByDate) {
      const key = dateKey(c.date) || 'Sans date';
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  }, [filteredByDate]);

  const stats = useMemo(() => {
    const total = filteredByDate.length;
    const depot = filteredByDate.filter(c => c.statut === 'depot').length;
    const en_cours = filteredByDate.filter(c => ['affecte', 'en_route'].includes(c.statut)).length;
    const livre = filteredByDate.filter(c => c.statut === 'livre').length;
    const echec = filteredByDate.filter(c => c.statut === 'echec').length;
    return { total, depot, en_cours, livre, echec };
  }, [filteredByDate]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main style={{ maxWidth: 980, margin: '0 auto', padding: '40px 20px' }}>
        <button onClick={() => navigate(-1)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#4F46E5', fontWeight: 600, fontSize: 14,
          marginBottom: 22, display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', padding: 0,
        }}>
          ← Retour
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', margin: 0 }}>Mes colis</h1>
            <div style={{ fontSize: 14, color: '#64748b', marginTop: 6 }}>Liste des colis que vous avez créés.</div>
          </div>

          {filtered.length > 0 && (
            <button type="button" onClick={clearMyColis} style={{
              padding: '12px 14px',
              background: '#F1F5F9',
              color: '#475569',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}>
              Effacer la liste
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 18 }}>
          <StatCard icon="📦" label="Total colis" value={stats.total} color="#1e293b" bg="#f1f5f9" />
          <StatCard icon="⏳" label="En attente" value={stats.depot} color="#D97706" bg="#FEF3C7" />
          <StatCard icon="🚚" label="En cours" value={stats.en_cours} color="#0284C7" bg="#E0F2FE" />
          <StatCard icon="✅" label="Livrés" value={stats.livre} color="#059669" bg="#D1FAE5" />
          <StatCard icon="❌" label="Échecs" value={stats.echec} color="#DC2626" bg="#FEE2E2" />
        </div>

        {availableDates.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#334155' }}>Filtrer par date</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="date"
                value={selectedDate}
                min={availableDates[availableDates.length - 1]}
                max={availableDates[0]}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '2px solid #e2e8f0',
                  background: '#fafafa',
                  color: '#1e293b',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              />

              {selectedDate && (
                <button type="button" onClick={() => setSelectedDate('')} style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: 'none',
                  background: '#F1F5F9',
                  color: '#475569',
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                  Effacer
                </button>
              )}
            </div>
          </div>
        )}

        {filtered.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 50,
            textAlign: 'center',
            color: '#94a3b8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>📦</div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Aucun colis créé</div>
            <div style={{ fontSize: 14 }}>Créez un dépôt pour voir vos colis apparaître ici.</div>

            <button onClick={() => navigate('/depot')} style={{
              marginTop: 18,
              padding: '12px 18px',
              background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)',
              color: 'white',
              border: 'none',
              borderRadius: 12,
              fontWeight: 800,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 12px rgba(79,70,229,0.3)',
            }}>
              📥 Nouveau dépôt
            </button>
          </div>
        ) : filteredByDate.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: 18,
            padding: 50,
            textAlign: 'center',
            color: '#94a3b8',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>📅</div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>Aucun colis pour cette date</div>
            <button onClick={() => setSelectedDate('')} style={{
              marginTop: 14,
              padding: '10px 14px',
              background: '#F1F5F9',
              color: '#475569',
              border: 'none',
              borderRadius: 12,
              fontWeight: 800,
              fontSize: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              Réinitialiser le filtre
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {Object.keys(groupedByDate).map(dateKey => (
              <div key={dateKey}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: '#334155',
                  marginBottom: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                }}>
                  {dateKey === 'Sans date' ? 'Sans date' : formatDate(dateKey)}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
                  {groupedByDate[dateKey].map(c => (
                    <ColisCard key={c.id} colis={c} readOnly />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Toast />
    </div>
  );
}
